import { ChevronDown, ChevronUp, Copy, Folder, Layers, Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAm, StatusPill } from "@/components/am/shared";

function Row({ level, x, depth, icon: Icon, actions, idx, count }) {
  const { sel, setSel } = useAm();
  const active = sel?.level === level && sel?.id === x.id;
  return (
    <div className={`group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors ${active ? "bg-emerald-500/10 ring-1 ring-emerald-500/40" : "hover:bg-slate-900"}`}
      style={{ paddingLeft: 8 + depth * 16 }} onClick={() => setSel({ level, id: x.id })} data-testid={`tree-${level}-${x.id}`}>
      <Icon className="h-4 w-4 shrink-0 text-slate-500" />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{x.name}</div>
        <StatusPill x={x} testid={`tree-status-${x.id}`} />
      </div>
      <div className="flex gap-0.5 opacity-60 group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
        {level !== "campaign" && <button title="Move up" disabled={idx === 0} onClick={() => actions.move(level, x, -1)} data-testid={`tree-up-${x.id}`} className="p-1 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>}
        {level !== "campaign" && <button title="Move down" disabled={idx === count - 1} onClick={() => actions.move(level, x, 1)} data-testid={`tree-down-${x.id}`} className="p-1 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>}
        <button title="Duplicate" onClick={() => actions.dup(level, x)} data-testid={`tree-dup-${x.id}`} className="p-1"><Copy className="h-3.5 w-3.5" /></button>
        {!x.on_meta && <button title="Delete draft" onClick={() => actions.del(level, x)} data-testid={`tree-del-${x.id}`} className="p-1 text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

export default function Tree({ onCampaignGone, onDuplicatedCampaign }) {
  const { tree, setTree, setSel } = useAm();
  const run = async (fn, msg) => { try { const r = await fn(); if (r.data?.campaign) setTree(r.data); if (msg) toast.success(msg); return r.data; } catch (e) { toast.error(errMsg(e)); } };
  const actions = {
    dup: async (level, x) => {
      const d = await run(() => api.post(`/ads-manager/${level}/${x.id}/duplicate`), `${level === "adset" ? "Ad set" : level === "ad" ? "Ad" : "Campaign"} duplicated as a new draft`);
      if (level === "campaign" && d) onDuplicatedCampaign(d.campaign.id);
    },
    del: async (level, x) => {
      if (!window.confirm(`Delete draft "${x.name}"?`)) return;
      if (level === "campaign") { await run(() => api.delete(`/ads-manager/campaign/${x.id}`), "Draft deleted"); onCampaignGone(); return; }
      await run(() => api.delete(`/ads-manager/${level}/${x.id}`), "Draft deleted"); setSel({ level: "campaign", id: tree.campaign.id });
    },
    move: async (level, x, dir) => {
      const list = level === "adset" ? tree.adsets : tree.adsets.find((a) => a.id === x.adset_id).ads;
      const ids = list.map((i) => i.id); const i = ids.indexOf(x.id); [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
      await run(() => api.post("/ads-manager/reorder", { level, ids }));
    },
  };
  return (
    <div className="space-y-0.5" data-testid="am-tree">
      <Row level="campaign" x={tree.campaign} depth={0} icon={Folder} actions={actions} />
      {tree.adsets.map((a, ai) => (
        <div key={a.id}>
          <Row level="adset" x={a} depth={1} icon={Layers} actions={actions} idx={ai} count={tree.adsets.length} />
          {a.ads.map((ad, di) => <Row key={ad.id} level="ad" x={ad} depth={2} icon={ImageIcon} actions={actions} idx={di} count={a.ads.length} />)}
          <button onClick={() => run(() => api.post(`/ads-manager/adsets/${a.id}/ads`), "Ad added")} data-testid={`tree-add-ad-${a.id}`}
            className="ml-12 text-xs text-emerald-600 hover:underline inline-flex items-center gap-1 py-1"><Plus className="h-3 w-3" />Add ad</button>
        </div>
      ))}
      <button onClick={() => run(() => api.post(`/ads-manager/campaigns/${tree.campaign.id}/adsets`), "Ad set added")} data-testid="tree-add-adset"
        className="ml-6 text-xs text-emerald-600 hover:underline inline-flex items-center gap-1 py-1"><Plus className="h-3 w-3" />Add ad set</button>
    </div>
  );
}
